import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

export default function Onboarding() {
  const { user, profile, company, refreshProfile, joinCompany } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [fullName,    setFullName]    = useState(profile?.full_name ?? '');
  const [companyName, setCompanyName] = useState(company?.name === 'My Company' ? '' : (company?.name ?? ''));
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? '');
    setCompanyName(company?.name === 'My Company' ? '' : (company?.name ?? ''));
  }, [profile, company]);

  useEffect(() => {
    if (!profile) return;
    const hasName = Boolean(profile.full_name?.trim());
    const adminNeedsCompany = profile.role === 'admin';
    const hasCompanyName = Boolean(company?.name?.trim()) && company?.name !== 'My Company';
    if (hasName && (!adminNeedsCompany || hasCompanyName)) {
      navigate('/', { replace: true });
    }
  }, [profile, company, navigate]);

  // Apply any pending company invite stored before login
  useEffect(() => {
    const applyPendingInvite = async () => {
      if (!profile) return;
      const raw = window.localStorage.getItem('pendingInvite');
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw) as { companyCode?: string; role?: string };
        if (!parsed.companyCode) return;
        await joinCompany(parsed.companyCode, parsed.role === 'tester' ? 'tester' : 'developer');
        window.localStorage.removeItem('pendingInvite');
        await refreshProfile();
      } catch {
        window.localStorage.removeItem('pendingInvite');
      }
    };
    applyPendingInvite();
  }, [profile, refreshProfile, joinCompany]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!fullName.trim()) { showToast('Please enter your full name.', 'error'); return; }
    if (profile?.role === 'admin' && !companyName.trim()) {
      showToast('Please enter your company name.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/api/profile', { full_name: fullName.trim() });
      if (profile?.role === 'admin' && company?.id) {
        await api.patch('/api/company', { name: companyName.trim() });
      }
      await refreshProfile();
      showToast('Profile completed! Welcome aboard.', 'success');
      navigate('/');
    } catch (err: any) {
      showToast(err.message || 'Failed to save changes. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="form-card" style={{ maxWidth: 560 }}>
        <h1 className="page-title" style={{ marginBottom: 8 }}>Complete Your Setup</h1>
        <p className="page-subtitle" style={{ marginBottom: 24 }}>
          We need a few details before you continue.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="fullName">Full Name</label>
            <input
              id="fullName"
              className="form-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
            />
          </div>

          {(!profile || profile.role === 'admin') && (
            <div className="form-group">
              <label className="form-label" htmlFor="companyName">Company Name</label>
              <input
                id="companyName"
                className="form-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your company name"
              />
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="btn-spinner" /> Saving...</> : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
